import { getApiData } from 'liyu-pc-base/api/api-model';
import { convertValueByClassName, isNotEmpty } from 'liyu-pc-base/utils/function';
import { useRequiredConditionStore } from 'liyu-pc-base/init-plugin';
import { resolveContextValue } from 'liyu-pc-base/components/FormRender/utils';

/**
 * remoteselectpro 的「展示 label 兜底解析」。
 *
 * 背景：FormTemplate 表格模式（FormTemplateTable）下 s-table 带分页，
 * 只有当前页的行会被挂载，RemoteSelectPro 仅在 onMounted 时通过
 * refresh()/externDataSource() 解析 label 并 emit('update:label')，
 * 因此非当前页的行（如 Excel 导入后的大量数据）永远拿不到
 * `model[field.name + '.label']`、name2、innerModel 等输出。
 *
 * 本模块与 RemoteSelectPro.vue 内部的查询参数构建逻辑保持一致
 * （getConditions / getSimpleSearchParams / getQueryParams /
 * externDataSource 的 in 条件），在不挂载组件的情况下，
 * 批量补齐指定行的 label 与原始选项。
 */

export interface RemoteSelectProField {
  name: string;
  postUrl?: string;
  valueKey?: string;
  labelKey?: string;
  labelKeyType?: number;
  labelKeyList?: any[];
  labelSpace?: string;
  mode?: string;
  init_model?: Record<string, any>;
  searchConditions?: any[];
  simpleSearchConditions?: any[];
  queryType?: string;
  required_condition?: any[];
  extendedFields?: string[];
  name2?: string;
}

export interface ResolveTarget {
  rowIndex: number;
  row: any;
}

export interface ResolveResult {
  /** default 模式为字符串，多选模式为字符串数组 */
  label: any;
  /** 原始选项：default 模式为单个对象，多选模式为对象数组 */
  option: any;
}

const getValueKey = (field: any): string => field.valueKey || 'id';
const getLabelKey = (field: any): string => field.labelKey || 'name';

/** 与 RemoteSelectPro.getColumnFields 保持一致 */
const getColumnFields = (field: any): string[] => {
  const list: string[] = [];
  const labelKeyList = field.labelKeyList || [];
  if (labelKeyList.length > 0 && field.labelKeyType !== 1) {
    list.push(getValueKey(field), ...labelKeyList);
  } else {
    list.push(getValueKey(field), getLabelKey(field));
  }
  if (field.extendedFields && field.extendedFields.length > 0) {
    list.push(...field.extendedFields);
  }
  return [...new Set(list)];
};

/** 与 RemoteSelectPro.get_sort_fields 保持一致 */
const getSortFields = (field: any): string[] => {
  const labelKeyList = field.labelKeyList || [];
  if (labelKeyList.length > 0 && field.labelKeyType !== 1) {
    return [...labelKeyList];
  }
  return [getLabelKey(field)];
};

/** 与 RemoteSelectPro.getConditionsItem 保持一致 */
const getConditionsItem = (value: any, list: any[], item: any) => {
  if (item.operator === 'not_blank_not_null') {
    list.push({
      field_name: item.field_name,
      operator: item.operator,
      value: [],
      format: item.format,
      class_name: item.class_name,
    });
  } else if (value instanceof Array) {
    const [first] = value;
    if (isNotEmpty(first)) {
      list.push({
        field_name: item.field_name,
        operator: item.operator,
        value: convertValueByClassName(value, item.class_name),
        format: item.format,
        class_name: item.class_name,
      });
    }
  } else if (isNotEmpty(value)) {
    let val = value;
    if (item.operator === 'in' || item.operator === 'notin') {
      if (value.includes('[')) {
        val = JSON.parse(value);
      } else if (value.includes(',')) {
        val = value.split(',');
      }
      if (item.class_name === 'int') {
        val = val.map(Number);
      }
    }
    list.push({
      field_name: item.field_name,
      operator: item.operator,
      value: convertValueByClassName(val instanceof Array ? val : [val], item.class_name),
      format: item.format,
      class_name: item.class_name,
    });
  }
};

/**
 * 与 RemoteSelectPro.resolveModelValue 保持一致。
 * searchType === 1 时按 key 同时处理 outModel 与 formModel：
 * outModel 中该字段有值（非 undefined / null）时优先，否则回退 formModel 的同名字段。
 * 不能用 `outModel ? outModel : formModel` 做对象级二选一（outModel 常为空对象但恒为真值）。
 */
const resolveModelValue = (
  selectName: any,
  formModel: any,
  outModel: any,
): { found: boolean; value: any } => {
  if (
    outModel &&
    Object.prototype.hasOwnProperty.call(outModel, selectName) &&
    outModel[selectName] !== undefined &&
    outModel[selectName] !== null
  ) {
    return { found: true, value: outModel[selectName] };
  }
  if (formModel && Object.prototype.hasOwnProperty.call(formModel, selectName)) {
    return { found: true, value: formModel[selectName] };
  }
  return { found: false, value: undefined };
};

/** 与 RemoteSelectPro.getRequiredConditionObj 保持一致 */
const getRequiredConditionObj = (field: any): any => {
  const required_condition = field.required_condition;
  const required_condition_obj: any = {};
  if (required_condition) {
    const store = useRequiredConditionStore();
    required_condition.forEach((item: any) => {
      const value = store.value_map[item.key];
      if (value !== undefined && value !== null) {
        required_condition_obj[item.field_name] = value;
      }
    });
  }
  return required_condition_obj;
};

/** 与 RemoteSelectPro.getConditions 保持一致（has_search=false） */
const getConditions = (
  field: any,
  formModel: any,
  outModel: any,
  context: any,
  baseKeyPath: any[],
): any[] => {
  const conditions: any[] = [];
  let search_conditions: any[] = [
    [
      {
        searchType: 3,
        field_name: 'is_active',
        operator: 'eq',
        value: [true],
        format: 'choices',
        class_name: 'bool',
      },
    ],
  ];
  if (field.searchConditions) {
    search_conditions = field.searchConditions;
  }
  search_conditions.forEach((item: any) => {
    const list: any[] = [];
    item.forEach((item1: any) => {
      if (item1.searchType === 1) {
        // outModel 优先、formModel 兜底，两者同时参与查找
        const resolved = resolveModelValue(item1.select_name, formModel, outModel);
        if (resolved.found) {
          getConditionsItem(resolved.value, list, item1);
        }
      } else if (item1.searchType === 2) {
        Object.keys(formModel).forEach((key: any) => {
          if (item1.select_name == key) {
            getConditionsItem(formModel[key], list, item1);
          }
        });
      } else if (item1.searchType === 3) {
        getConditionsItem(item1.value, list, item1);
      } else if (item1.searchType === 4) {
        getConditionsItem(
          resolveContextValue(item1.select_name, context, ['formModel', ...baseKeyPath]),
          list,
          item1,
        );
      } else {
        getConditionsItem(item1.value, list, item1);
      }
    });
    conditions.push(list);
  });
  return conditions;
};

/** 与 RemoteSelectPro.getSimpleSearchParams 保持一致（has_search=false） */
const getSimpleSearchParams = (
  field: any,
  formModel: any,
  outModel: any,
  context: any,
  baseKeyPath: any[],
): any => {
  const params: any = {};
  if (field.simpleSearchConditions) {
    field.simpleSearchConditions.forEach((item: any) => {
      if (item.searchType === 1) {
        // outModel 优先、formModel 兜底，两者同时参与查找
        const resolved = resolveModelValue(item.select_name, formModel, outModel);
        if (resolved.found) {
          params[item.field_name] = resolved.value;
        }
      } else if (item.searchType === 2) {
        Object.keys(formModel).forEach((key: any) => {
          if (item.select_name == key) {
            params[item.field_name] = formModel[key];
          }
        });
      } else if (item.searchType === 3) {
        params[item.field_name] = item.value;
      } else if (item.searchType === 4) {
        params[item.field_name] = resolveContextValue(
          item.select_name,
          context,
          ['formModel', ...baseKeyPath],
        );
      }
    });
  }
  return params;
};

/**
 * 与 RemoteSelectPro.getQueryParams 保持一致。
 * baseKeyPath 为当前行对应的完整路径段（含 FormTemplate 字段名与行号），
 * 例如 [...baseKeyPath, field.name, rowIndex]。
 */
const buildRowQueryParams = (
  field: any,
  formModel: any,
  outModel: any,
  context: any,
  baseKeyPath: any[],
): any => {
  const searchConfig: any = {
    current: 1,
    pageSize: 200,
    is_pagination: true,
    request_source: 'strict',
    is_active: true,
    ...(field.init_model || {}),
    column_fields: getColumnFields(field),
    sort_fields: field.init_model?.sort_fields || getSortFields(field),
    conditions: [],
    simpleSearchParams: {},
  };
  const required_condition = getRequiredConditionObj(field);
  if (field.queryType === 'simple') {
    const simpleSearchParams = getSimpleSearchParams(field, formModel, outModel, context, baseKeyPath);
    const params = { ...searchConfig, ...simpleSearchParams, required_condition };
    delete params.conditions;
    delete params.simpleSearchParams;
    return params;
  }
  const conditions = getConditions(field, formModel, outModel, context, baseKeyPath);
  const params = { ...searchConfig, conditions, required_condition };
  delete params.simpleSearchParams;
  return params;
};

/** 与 RemoteSelectPro.externDataSource 的 in 条件保持一致，class_name 按值类型自适应 */
const appendInCondition = (params: any, field: any, values: any[]) => {
  const isAllNumeric = values.every(
    v => typeof v === 'number' || (typeof v === 'string' && v.trim() !== '' && !isNaN(Number(v))),
  );
  const class_name = isAllNumeric ? 'int' : 'str';
  if (field.queryType === 'simple') {
    params[getValueKey(field)] = values.length > 0 ? values.join(',') : '';
  } else {
    if (params.conditions && params.conditions.length > 0) {
      params.conditions[0].push({
        class_name,
        operator: 'in',
        value: values,
        format: 'remote',
        field_name: getValueKey(field),
      });
    } else {
      params.conditions = [[]];
      params.conditions[0].push({
        class_name,
        operator: 'in',
        value: values,
        format: 'remote',
        field_name: getValueKey(field),
      });
    }
  }
  return params;
};

/** 与 RemoteSelectPro.initDataSource 的值转换保持一致 */
const normalizeValues = (value: any, field: any): any[] => {
  const convertValue = (item: any) => {
    if (getValueKey(field) === 'id') {
      if (isNaN(Number(item))) {
        return item;
      }
      return Number(item);
    }
    return item;
  };
  if (field.mode === 'default' || !field.mode) {
    if (value || value === 0) {
      return [convertValue(value)];
    }
    return [];
  }
  if (typeof value === 'string') {
    return (value as string).split(',').map(convertValue);
  }
  if (value instanceof Array) {
    return value.map(convertValue);
  }
  if (value != null) {
    return [convertValue(value)];
  }
  return [];
};

/** 与 RemoteSelectPro.datasource computed 的 label 拼接保持一致 */
const getOptionLabel = (field: any, opt: any): string => {
  if (field.labelKeyList && field.labelKeyList.length > 0 && field.labelKeyType !== 1) {
    const parts: string[] = [];
    for (const key of field.labelKeyList) {
      const v = opt[key];
      parts.push(v !== undefined && v !== null ? String(v) : '');
    }
    return parts.join(field.labelSpace || '-');
  }
  const v = opt[getLabelKey(field)];
  return v !== undefined && v !== null ? String(v) : '';
};

/**
 * 批量解析 remoteselectpro 的展示 label（无需挂载组件）。
 *
 * 参数相同的行（同一字段、相同查询条件）会合并为一次请求；
 * 超过 100 个值时自动分批，避免后端 in 条件过长。
 *
 * @returns rowIndex -> ResolveResult；未匹配到返回 null，不存在的行不写入。
 */
export async function resolveRemoteSelectProLabels(options: {
  field: any;
  targets: ResolveTarget[];
  baseKeyPath: any[];
  context: any;
}): Promise<Record<number, ResolveResult | null>> {
  const { field, targets, baseKeyPath, context } = options;
  if (!field.postUrl || !targets || targets.length === 0) {
    return {};
  }

  // 按查询参数分组：参数相同的行合并请求
  const groups = new Map<
    string,
    { params: any; items: { rowIndex: number; values: any[] }[] }
  >();
  for (const target of targets) {
    const rowKeyPath = [...baseKeyPath, target.rowIndex];
    const params = buildRowQueryParams(field, target.row || {}, {}, context, rowKeyPath);
    const values = normalizeValues(target.row?.[field.name], field);
    if (values.length === 0) {
      continue;
    }
    const key = JSON.stringify(params);
    if (!groups.has(key)) {
      groups.set(key, { params, items: [] });
    }
    groups.get(key)!.items.push({ rowIndex: target.rowIndex, values });
  }

  const result: Record<number, ResolveResult | null> = {};
  const foundMap = new Map<string, { label: string; option: any }>();

  for (const group of groups.values()) {
    const allValues = [...new Set(group.items.flatMap(i => i.values))];
    if (allValues.length === 0) {
      continue;
    }
    // 分批请求，每批 100 个值
    for (let i = 0; i < allValues.length; i += 100) {
      const chunk = allValues.slice(i, i + 100);
      const params = JSON.parse(JSON.stringify(group.params));
      appendInCondition(params, field, chunk);
      let dataList: any[] = [];
      try {
        const res = await getApiData(params, field.postUrl, false, { skip_error: true });
        dataList = res?.data || [];
      } catch (e) {
        console.error('resolveRemoteSelectProLabels fetch error:', field.name, e);
      }
      for (const opt of dataList) {
        const v = opt[getValueKey(field)];
        const key = String(v);
        if (!foundMap.has(key)) {
          foundMap.set(key, { label: getOptionLabel(field, opt), option: opt });
        }
      }
    }

    for (const item of group.items) {
      const found = item.values
        .map(v => foundMap.get(String(v)))
        .filter((r): r is { label: string; option: any } => !!r);
      if (found.length === 0) {
        result[item.rowIndex] = null;
        continue;
      }
      if (field.mode === 'default' || !field.mode) {
        const first = found[0];
        result[item.rowIndex] = { label: first.label, option: first.option };
      } else {
        result[item.rowIndex] = {
          label: found.map(f => f.label),
          option: found.map(f => f.option),
        };
      }
    }
  }
  return result;
}
